import { Button } from '@crowdfpl/ui-web/button';
import { Dialog, DialogContent, DialogTitle } from '@crowdfpl/ui-web/dialog';
import { ChevronLeft, ChevronRight, Crown, Sparkles, Swords, Users } from 'lucide-react';
import { useCallback, useEffect, useRef, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CaptainPreview,
  DuelCrowdPreview,
  GameweekBadge,
  LeaderboardPreview,
  ModeTile,
} from '@/components/onboarding/v2';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import { useSwipe } from '@/hooks/utils/use-swipe';
import { ONBOARDING_TOTAL_STEPS, useOnboarding } from '@/stores/onboarding';

const CTA_LABELS = ['Show Me How', 'See It In Action', 'Start Competing'] as const;

export function OnboardingModal(): ReactElement {
  const navigate = useNavigate();
  const { isOpen, hasCompleted, currentStep, hide, complete, nextStep, prevStep, setStep } = useOnboarding();
  const wasCompletedAtOpen = useRef(false);
  const lastTrackedStep = useRef<number | null>(null);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === ONBOARDING_TOTAL_STEPS - 1;

  const trackIfNew = useCallback((event: string, data?: Record<string, unknown>) => {
    if (wasCompletedAtOpen.current) return;
    if (data !== undefined) {
      trackUmamiEvent(event, data);
      return;
    }
    trackUmamiEvent(event);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      lastTrackedStep.current = null;
      return;
    }

    if (lastTrackedStep.current === null) {
      wasCompletedAtOpen.current = hasCompleted;
      trackIfNew('onboarding_v2_start');
    }

    if (lastTrackedStep.current !== currentStep) {
      trackIfNew('onboarding_v2_step_view', { step: currentStep + 1 });
      lastTrackedStep.current = currentStep;
    }
  }, [isOpen, currentStep, hasCompleted, trackIfNew]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      trackIfNew('onboarding_v2_complete', { source: 'cta' });
      complete();
      navigate('/duels');
      return;
    }
    nextStep();
  }, [isLastStep, complete, nextStep, navigate, trackIfNew]);

  const handlePrev = useCallback(() => {
    if (!isFirstStep) prevStep();
  }, [isFirstStep, prevStep]);

  const handleSwipeLeft = useCallback(() => {
    if (!isLastStep) nextStep();
  }, [isLastStep, nextStep]);

  const handleSwipeRight = useCallback(() => {
    if (!isFirstStep) prevStep();
  }, [isFirstStep, prevStep]);

  const swipeRef = useSwipe<HTMLDivElement>({
    onSwipeLeft: handleSwipeLeft,
    onSwipeRight: handleSwipeRight,
    threshold: 50,
  });

  const handleSkip = useCallback(() => {
    trackIfNew('onboarding_v2_skip', { step: currentStep + 1 });
    trackIfNew('onboarding_v2_complete', { source: 'skip' });
    complete();
  }, [complete, currentStep, trackIfNew]);

  const handleClose = useCallback(() => {
    hide();
  }, [hide]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 gap-0 overflow-hidden border-border/50 rounded-2xl [&>button:last-child]:hidden"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Welcome to CrowdFPL</DialogTitle>

        <div className="absolute right-4 top-4 z-10">
          <button
            type="button"
            onClick={handleSkip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Skip
          </button>
        </div>

        <div
          ref={swipeRef}
          key={currentStep}
          className="px-4 sm:px-6 pt-10 pb-2 min-h-[400px] flex flex-col justify-center animate-fade-in touch-pan-y"
        >
          {currentStep === 0 && (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold text-center">Prove Your FPL Calls</h2>
              <p className="text-sm text-muted-foreground text-center mt-1">
                Compare your ideas with thousands of FPL managers.
              </p>
              <div className="mt-5 grid grid-cols-3 gap-1.5 sm:gap-3">
                <ModeTile icon={Swords} title="Player Duels" hint="Predict player vs player" tone="primary" />
                <ModeTile icon={Crown} title="Captain" hint="Pick the best captain" tone="gold" />
                <ModeTile icon={Users} title="Leagues" hint="Compete with friends" tone="competitive" />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-5 max-w-xs mx-auto">
                See where you agree, and where you beat the crowd.
              </p>
            </>
          )}

          {currentStep === 1 && (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold text-center">Make Your Calls</h2>
              <p className="text-sm text-muted-foreground text-center mt-1">Before every Gameweek deadline</p>
              <div className="mt-4 space-y-2.5">
                <GameweekBadge />
                <DuelCrowdPreview />
                <CaptainPreview />
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold text-center">Beat the Crowd</h2>
              <p className="text-sm text-muted-foreground text-center mt-1">
                Rise through the global and league leaderboards.
              </p>
              <div className="mt-4">
                <LeaderboardPreview />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-center pb-4">
          {Array.from({ length: ONBOARDING_TOTAL_STEPS }, (_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setStep(index)}
              className="p-3 -mx-1 flex items-center justify-center"
              aria-label={`Go to step ${index + 1}`}
            >
              <span
                className={cn(
                  'h-2 rounded-full transition-all block',
                  index === currentStep
                    ? 'w-6 bg-primary'
                    : index < currentStep
                      ? 'w-2 bg-primary/50'
                      : 'w-2 bg-muted-foreground/30',
                )}
              />
            </button>
          ))}
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" onClick={handlePrev} disabled={isFirstStep} className="flex-1">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <Button onClick={handleNext} className="flex-[1.4]">
            {CTA_LABELS[currentStep]}
            {isLastStep ? <Sparkles className="h-4 w-4 ml-1" /> : <ChevronRight className="h-4 w-4 ml-1" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
